<template>
    <div>
        <!-- SEARCH -->
        <div v-if="chosenAddress">
            <div class="hero-address-content">
                <h4 class="mb-0 text-center">
                    <span>
                        {{ lang ('Din adress','Your address') }}: {{ chosenAddress.fullAddress }}
                    </span>
                </h4>
                <a
                    v-if="chosenAddress.canUnChoose"
                    v-tooltip.bottom="lang('Återställ valet av adress','Reset selected address')"
                    :href="route('front.addresses.unchoose')"
                    class="ml-lg w-2xl h-2xl rounded-full bg-accent-yellow text-xs text-neutral-darker flex-none flex justify-center items-center"
                >
                    <font-awesome-icon icon="times" />
                </a>
            </div>
        </div>
        <div v-else>
            <div class="form-group search has-icon relative">
                <input
                    id="address-search"
                    ref="input"
                    v-model="query"
                    type="text"
                    name="search"
                    aria-label="Sök"
                    :placeholder="searchInputPlaceholder"
                    class="input placeholder-shown has-icon-left"
                    :disabled="disabled"
                    autocomplete="off"
                    @paste="update"
                    @input="update"
                    @keydown.down="down"
                    @keydown.up="up"
                    @keydown.enter="hit"
                >
                <a
                    class="icon-wrapper"
                >
                    <font-awesome-icon
                        v-if="loading"
                        icon="spinner"
                        pulse
                        spin
                        class="text-sm"
                    />
                    <font-awesome-icon
                        v-else
                        icon="search"
                        class="text-sm"
                    />
                </a>
                <button
                    v-if="query"
                    type="button"
                    class="absolute flex items-center pin-t pin-b pin-r mr-md text-neutral-medium hover:text-neutral-dark"
                    @click="reset"
                >
                    <font-awesome-icon icon="times-circle" />
                </button>
            </div>
            <div
                v-if="hasItems && ! loading"
                class="results"
            >
                <div class="w-full">
                    <ul class="list-reset my-lg pl-lg pr-xl">
                        <li
                            v-for="(item, index) in items"
                            :class="activeClass(index)"
                            @mousedown="hit"
                            @mousemove="setActive(index)"
                        >
                            <a class="inline-block w-full py-md px-lg text-md font-bold">
                                {{ item.fullAddress }}
                            </a>
                        </li>
                    </ul>
                </div>
            </div>
            <div v-else class="results">
                <div
                    v-if="query && ! loading && hasSearched"
                    class="p-xl"
                >
                    <div class="nohits">
                        <h4>{{ lang('Inga träffar på din adress','No hits on your address') }} 😢</h4>
                        <p class="text-neutral-medium">
                            {{ lang(
                                'Borde vi ha din adress i våra system?',
                                'Should we have you address in our system?'
                            ) }}
                            <a :href="route('front.customer-service.contact')"> {{ lang('Kontakta kundservice','Contact customer service') }}</a>
                            {{ lang('så löser vi det.','to fix it.') }}
                            <font-awesome-icon
                                v-tooltip="missingAddressInSystemTooltip"
                                icon="info-circle"
                                class="text-accent-yellow text-md"
                            />
                            <br><br>
                            {{ lang(
                                'Vill du ansluta din förening eller företag till Ownits nät? Läs mer på ',
                                'Want to connect you cooperative or company to Ownits network? Read more at our '
                            ) }}
                            <a :href="pageLinks.brf">{{ lang('Fastighetsägare/BRF','Cooperative') }}</a>
                            {{ lang('eller','or') }}
                            <a :href="pageLinks.company"> {{ lang('Företag','Company page') }}</a>.
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <!-- CHOOSE APARTMENT / ALREADY CUSTOMER MODAL -->
        <standard-modal
            :name="chooseApartmentNumberModalName"
            :show-close-button="showCloseButton"
            :open-on-page-load="showModalOnLoad"
            @hide="unchooseChosenAddress"
        >
            <template #title>
                <h3 v-if="userIsCustomer" class="mb-0">{{ lang('Det finns redan en kund på den här adressen','Theres already a customer on this address') }}!</h3>
                <div v-else class="flex items-center">
                    <h3 class="mb-0">{{ lang('Ange ditt lägenhetsnummer','Submit your apartment number') }}</h3>
                    <font-awesome-icon
                        v-tooltip="apartmentNumberTooltipText"
                        icon="info-circle"
                        class="text-accent-yellow text-lg ml-lg"
                    />
                </div>
            </template>
            <div class="mt-3xl">
                <div v-if="userIsCustomer">
                    <p>
                        {{ lang('Logga in för att se ditt utbud och börja beställa','Log in to see whats available to you and to place an order') }}.
                        <br><br>
                        {{ lang(
                            'Inte du? Ibland händer det att en tidigare kund flyttat ut utan att säga upp sitt abonnemang -',
                            'Not you? Sometimes it happens that a previous customer moves out without terminating their subscription'
                        ) }}
                        {{ lang(
                            ' isåfall kan du välja fortsätt och skapa ett nytt konto i samband med din beställning',
                            ' if that\'s the case, click continue and create a new account at the checkout'
                        ) }}.
                    </p>
                    <div class="text-center mt-4xl mb-2xl">
                        <a :href="continueOrderUrl" class="btn btn-lg is-primary mr-lg mb-lg md:mb-0">{{ lang('Fortsätt','Continue') }}</a>
                        <button class="btn btn-lg is-primary" @click="showLoginModal">{{ lang('Logga in','Log in') }}</button>
                    </div>
                    <hr class="my-xl">
                    <p class="mb-0">
                        {{ lang(
                            'Valt fel adress eller lägenhetsnummer',
                            'Submited the wrong address or apartment number'
                        ) }}? <a :href="route('front.addresses.unchoose')">{{ lang('Återställ din valda adress','Reset submited address') }}.</a>
                    </p>
                </div>
                <div v-else>
                    <div v-if="apartmentNumbers.length > 0">
                        <select-input
                            v-model="apartmentNumberAddressId"
                            name="apartment_number"
                            value-key="addressId"
                            label-key="apartmentNumber"
                            :options="apartmentNumbers"
                            :placeholder="lang('Välj','Select')"
                        />
                        <h4>
                            {{ lang('Jag hittar inte mitt lägenhetsnummer i listan','I can\'t find my apartment number') }}
                        </h4>
                        <p>
                            {{ lang('Ditt lägenhetsnummer kan vara på olika format. Om du inte hittar din lägenhet i listan så kan du kolla på ditt bostadskontrakt och leta efter "objektnummer"', 'Your apartment number format can vary. If you can\'t find your apartment in the list, check your lease and look for "object number"') }}
                        </p>
                    </div>
                    <div v-else-if="isClosedNetAddress" class="mb-xl">
                        <!-- Closed net and no apartments! -->
                        <p>
                            {{ lang(
                                'Vi kunde inte hitta några lägenhetsnummer på din adress.',
                                "We weren't able to find any apartment numbers on this address."
                            ) }}
                        </p>
                    </div>

                    <div
                        v-if="apartmentNumbers.length > 0"
                        class="flex justify-end my-2xl"
                    >
                        <form
                            v-loading="formIsLoading"
                            class="w-full md:w-auto"
                            @submit.prevent="chooseApartmentNumber"
                        >
                            <button
                                type="submit"
                                class="btn btn-lg is-primary w-full md:w-auto"
                                :disabled="! canContinue"
                            >
                                {{ lang('Fortsätt','Continue') }}
                            </button>
                        </form>
                    </div>
                    <hr>
                    <p>
                        {{ lang(
                            'Har du valt fel adress eller kommer du inte vidare',
                            'Have you submitted the wrong address or can\'t proceed'
                        ) }}?
                        <a @click="unchooseChosenAddress">{{ lang('Återställ din valda adress','Reset address') }}.</a>
                    </p>
                    <span v-if="isClosedNetAddress">
                        {{ lang(
                            'Behöver du hjälp eller hittar du inte ditt lägenhetsnummer i listan',
                            'Need help or can\'t find your apartment number') }}?
                    </span>
                    <span v-else>
                        {{ lang('Behöver du hjälp','Need help') }}?
                    </span>
                    <a :href="route('front.customer-service.contact')">
                        {{ lang('Kontakta kundservice','Contact customer service') }}
                    </a>
                </div>
            </div>
        </standard-modal>

        <error-modal :error="error" :on-close-callback="onErrorModalCloseCallback" />
    </div>
</template>

<script>
    import VueTypeahead from '../../Atlantis/SuperSearcher/vue-typeahead.vue';

    export default {
        extends: VueTypeahead,
        props: {
            initialUserIsCustomer: {
                type: Boolean,
                required: false,
                default: false,
            },
            initialApartmentNumbers: {
                type: Array,
                required: false,
                default: () => {
                    return []; // doesn't work
                },
            },
            initialChosenAddress: {
                type: Object,
                required: false,
                default: null,
            },
            chosenApartmentNumber: {
                type: Object,
                required: false,
                default: null,
            },
            autoFocusAddressField: {
                type: Boolean,
                required: false,
                default: false,
            },
            callToActionLink: {
                type: String,
                required: false,
                default: null,
            },
            pageLinks: {
                type: Object,
                required: true,
            },
        },
        data() {
            return {
                src: route('front.api.addresses.index'),
                limit: 20,
                minChars: 2,
                chosenAddress: this.initialChosenAddress,
                selectFirst: true,
                formIsLoading: false,
                disabled: false, // if the input should be disabled (eg. when loading modals etc)
                apartmentNumbers: this.initialApartmentNumbers || [],
                apartmentNumberAddressId: '',
                chooseApartmentNumberModalName: 'choose-apartment-number',
                userIsCustomer: this.initialUserIsCustomer,
                error: null,
                redirectUrl: this.callToActionLink,
                onErrorModalCloseCallback: null,
            };
        },
        computed: {
            showCloseButton() {
                return ! this.userIsCustomer;
            },
            continueOrderUrl() {
                if (this.redirectUrl) {
                    return this.redirectUrl;
                }

                return window.location;
            },
            searchInputPlaceholder() {
                return window.utils.breakpoint('md')
                    ? lang('Sök på din adress för att se vad vi kan erbjuda dig','Search for your address to see what we can offer you')
                    : lang('Sök på din adress','Search for your address');
            },
            canContinue() {
                if (! this.apartmentNumberAddressId) {
                    return false;
                }

                return true;
            },
            apartmentNumberTooltipText() {
                return lang(
                    'Ditt lägenhetsnummer kan vara på olika format beroende på din förening. Kolla med din hyresvärd/förening om vilket lägenhetsnummer du har. Om du bor i villa/radhus ska du inte ange något lägenhetsnummer',
                    'Your apartment number format can vary. Please check with your cooperative/landlord. If you live in a house, don\'t fill this in.'
                );
            },
            isClosedNetAddress() {
                return this.chosenAddress && this.chosenAddress.isClosedNet;
            },
            showModalOnLoad() {
                // We need to check if we have available apartment numbers
                // but not yet choosed one. This case can happen eg. if the
                // user closes the modal button via page reload before selecting an
                // apartment number
                if (this.chosenAddress && ! this.chosenApartmentNumber) {
                    return true;
                }

                return false;
            },
            icon() {
                if (this.loading) {
                    return 'spinner';
                }

                return '';
            },
            iconClasses() {
                if (this.isDirty) {
                    return 'cursor-pointer';
                }

                return '';
            },
            missingAddressInSystemTooltip() {
                return lang(
                    'Bor du i en fastighet som är antingen är direkt ansluten till Ownit eller ansluten via ett öppet fibernät nät så bör din adress normalt finnas i våra system men ibland kan det bli fel.',
                    'Your address should normally be in our system if you are living in a property directly connected to Ownit or connected through open fiber however sometimes some address are missing.'
                );
            },
        },
        watch: {
            error(value) {
                if (value) {
                    this.$modal.show('error-modal');
                }
            },
            items(items) {
                let numberOfHits = 0;
                if (items && items.length > 0) {
                    numberOfHits = items.length;
                }

                this.$GtmTracker.addressSearch(this.query, numberOfHits);
            },
        },
        mounted() {
            if (this.autoFocusAddressField) {
                this.scrollItAndFocus('#address-search');
            }

            EventBus.$on('redirectAfterSearch', redirectUrl => {
                this.redirectUrl = redirectUrl;
            });
        },
        methods: {
            async onHit(item) {
                this.loading = true;
                this.disabled = true;

                window.dataLayer.push({
                    'addressSearchHit': item.addressId,
                });

                try {
                    const response = await axios.post(route('front.api.addresses.choose'), {
                        addressId: item.addressId,
                    });

                    if (response.data.redirect) {
                        this.$GtmTracker.addressChosen(item.fullAddress, 'From address');

                        return window.location = this.continueOrderUrl;
                    }

                    this.$GtmTracker.addressApartmentList(this.query, response.data.apartmentNumbers.length);

                    this.apartmentNumbers = response.data.apartmentNumbers;
                    this.$modal.show(this.chooseApartmentNumberModalName);
                    this.chosenAddress = item;
                } catch (error) {
                    this.error = error.response.data.message;
                }
                this.disabled = false;
                this.loading = false;
            },
            async chooseApartmentNumber() {
                this.formIsLoading = true;

                try {
                    const response = await axios.post(route('front.api.apartment-numbers.choose'), {
                        apartmentNumberAddressId: this.apartmentNumberAddressId,
                    });

                    if (response.data.exists) {
                        // User is customer and should login!
                        this.userIsCustomer = true;
                        this.formIsLoading = false;

                        this.$GtmTracker.addressHasCustomer(this.chosenAddress);
                    } else if (response.data.success) {
                        this.$GtmTracker.addressChosen(this.chosenAddress, 'From list of apartments');

                        window.location = this.continueOrderUrl;
                    } else if (response.data.error) {
                        this.error = response.data.error;
                        this.formIsLoading = false;
                    }
                } catch (error) {
                    this.error = error.response.data.message;
                    this.formIsLoading = false;

                    // Handle an exception be reloading page when user clicks error modal "Close"
                    if (error.response.status === 500) {
                        const url = this.continueOrderUrl;

                        this.onErrorModalCloseCallback = function () {
                            window.location = url;
                        };
                    }
                }
            },

            activeClass(index) {
                return {
                    'bg-neutral-lighter rounded': this.current === index,
                };
            },

            showLoginModal() {
                this.$modal.hide(this.chooseApartmentNumberModalName);
                this.$modal.show('login-modal');
            },

            unchooseChosenAddress() {
                if (this.userIsCustomer) {
                    return;
                }

                window.location = route('front.addresses.unchoose');
            },

            errorCallback(error) {
                // The endpoint can return 403 if the scraping protection triggers, in which case we should
                // refresh the page to let ordinary users try again in case of a false positive
                if (error.response.status === 403
                    && Object.prototype.hasOwnProperty.call(error.response.data, 'refreshPage')
                ) {
                    window.location.reload();
                }
            },
        },
    };
</script>
